import { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { getTeams, getPlayers } from "../lib/api";

export const SearchBar = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const blurTimer = useRef(null);

  const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams });
  const { data: players = [] } = useQuery({ queryKey: ["players-all"], queryFn: () => getPlayers() });

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return { teams: [], players: [] };
    return {
      teams: teams
        .filter((t) => t.fullName.toLowerCase().includes(term) || t.abbr.toLowerCase().includes(term))
        .slice(0, 5),
      players: players
        .filter((p) => p.name.toLowerCase().includes(term))
        .slice(0, 5),
    };
  }, [q, teams, players]);

  const go = (path) => {
    setQ("");
    setOpen(false);
    navigate(path);
  };

  const hasResults = results.teams.length > 0 || results.players.length > 0;

  return (
    <div className="relative">
      <div className="flex items-center gap-2 border border-border bg-secondary px-3 h-9 w-40 sm:w-64 focus-within:border-primary transition-colors duration-150">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
          placeholder="Search teams, players…"
          className="bg-transparent outline-none text-sm w-full placeholder:text-muted-foreground"
        />
      </div>

      {open && q.trim() && (
        <div
          data-testid="search-results"
          className="absolute right-0 mt-1 w-72 bg-popover border border-border z-[60] max-h-96 overflow-y-auto"
          onMouseDown={() => clearTimeout(blurTimer.current)}
        >
          {!hasResults && <p className="px-4 py-3 text-sm text-muted-foreground">No matches found.</p>}

          {results.teams.length > 0 && (
            <div>
              <p className="label-caps px-4 pt-3 pb-1">Teams</p>
              {results.teams.map((t) => (
                <button
                  key={t.id}
                  data-testid={`search-team-${t.id}`}
                  onClick={() => go(`/team/${t.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors duration-150 text-left"
                >
                  <img src={t.logo} alt={t.abbr} className="w-6 h-6 object-contain" />
                  <span className="font-semibold text-sm">{t.fullName}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{t.wins}-{t.losses}</span>
                </button>
              ))}
            </div>
          )}

          {results.players.length > 0 && (
            <div>
              <p className="label-caps px-4 pt-3 pb-1">Players</p>
              {results.players.map((p) => (
                <button
                  key={p.id}
                  data-testid={`search-player-${p.id}`}
                  onClick={() => go(`/team/${p.team}`)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors duration-150 text-left"
                >
                  <img src={p.teamLogo} alt={p.team} className="w-6 h-6 object-contain" />
                  <span className="text-sm">
                    <span className="font-semibold">{p.name}</span>
                    <span className="text-muted-foreground ml-2">{p.position}</span>
                  </span>
                  <span className="ml-auto font-display text-primary">{p.headline}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
